/**
 * SignalR transport for the chat hub. A port of mobile
 * `chat_hub_service.dart` (`ChatHubService`) to `@microsoft/signalr`.
 *
 * Wire contract (verbatim from mobile — the concrete `ChatHub` class
 * cannot auto-strip `Async`, so `TypingAsync`/`HeartbeatAsync` go verbatim):
 * incoming `ReceiveMessage` / `ChatEvent` / `UserTyping` / `MessagesRead` /
 * `UserOnline` / `UserOffline`; outgoing `TypingAsync` / `MarkAsRead` /
 * `HeartbeatAsync` / `JoinConversation` / `LeaveConversation`.
 */
import * as signalR from '@microsoft/signalr'
import { hubAccessToken, hubUrl } from '@/lib/hubs'
import { newClientMessageId } from './outbox'

export const CHAT_DEVICE_ID_KEY = 'lm-chat-device-id'

/** Stable per-browser device id, sent as `?deviceId=` like mobile. */
export function getChatDeviceId(): string {
  try {
    const stored = localStorage.getItem(CHAT_DEVICE_ID_KEY)
    if (stored) return stored
    const id = newClientMessageId()
    localStorage.setItem(CHAT_DEVICE_ID_KEY, id)
    return id
  } catch {
    return newClientMessageId()
  }
}

export interface ChatHubEvents {
  onMessage?: (message: Record<string, unknown>) => void
  onChatEvent?: (event: Record<string, unknown>) => void
  onTyping?: (conversationId: string, userId: string) => void
  onMessagesRead?: (conversationId: string, userId: string) => void
  onUserOnline?: (userId: string) => void
  onUserOffline?: (userId: string) => void
  /** Transport reconnected: group membership is gone, re-join (mobile `onReconnected`). */
  onReconnected?: () => void
  onError?: (error: unknown) => void
}

export type HubConnectionFactory = () => Pick<
  signalR.HubConnection,
  'on' | 'invoke' | 'start' | 'stop' | 'onclose' | 'onreconnecting' | 'onreconnected'
>

function defaultBuildConnection(): signalR.HubConnection {
  const deviceId = getChatDeviceId()
  const url = deviceId ? `${hubUrl('chat')}?deviceId=${encodeURIComponent(deviceId)}` : hubUrl('chat')
  return new signalR.HubConnectionBuilder()
    .withUrl(url, {
      accessTokenFactory: hubAccessToken,
      transport:
        signalR.HttpTransportType.WebSockets |
        signalR.HttpTransportType.ServerSentEvents,
    })
    .withAutomaticReconnect()
    .configureLogging(signalR.LogLevel.Warning)
    .build()
}

function asRecord(value: unknown): Record<string, unknown> | null {
  return value != null && typeof value === 'object' ? (value as Record<string, unknown>) : null
}

function str(value: unknown): string | undefined {
  if (typeof value === 'string') return value
  if (typeof value === 'number' || typeof value === 'boolean') return String(value)
  return undefined
}

/**
 * Mobile `hub_event_parser.dart` (`hubEventString`): canonical object
 * payloads or legacy positional args — malformed payloads are null.
 */
export function hubEventString(args: unknown[], key: string, position = 0): string | null {
  if (args.length === 0) return null
  const first = asRecord(args[0])
  if (first) {
    const value = str(first[key])
    return value ?? null
  }
  return typeof args[position] === 'string' ? (args[position] as string) : null
}

function hubEventPair(args: unknown[], keyA: string, keyB: string): [string, string] | null {
  const a = hubEventString(args, keyA, 0)
  const b = hubEventString(args, keyB, 1)
  return a != null && b != null ? [a, b] : null
}

export interface ParsedChatEvent {
  eventId: string
  eventType: string
  cursor: number
  message: Record<string, unknown>
}

/**
 * Mobile `ChatHubProvider._applyEvent` envelope read: `message` is
 * required (else the event is malformed); `eventId` may be empty (then
 * the row is reduced without persisting); `cursor` defaults to 0.
 */
export function parseChatEvent(value: unknown): ParsedChatEvent | null {
  const e = asRecord(value)
  if (!e) return null
  const message = asRecord(e.message)
  if (!message) return null
  const cursor = e.cursor
  return {
    eventId: str(e.eventId) ?? '',
    eventType: str(e.eventType) ?? '',
    cursor: typeof cursor === 'number' && Number.isInteger(cursor) ? cursor : 0,
    message,
  }
}

export class ChatHubClient {
  private connection: ReturnType<HubConnectionFactory> | null = null
  private connected = false

  constructor(
    private events: ChatHubEvents = {},
    private readonly build: HubConnectionFactory = defaultBuildConnection,
  ) {}

  get isConnected(): boolean {
    return this.connected
  }

  /** Mobile `_wireEvents`: replace the callback set (the session owns reduction). */
  setEvents(events: ChatHubEvents): void {
    this.events = events
  }

  async connect(): Promise<void> {
    if (this.connection && this.connected) return
    const connection = this.build()
    this.connection = connection
    this.wire(connection)
    try {
      await connection.start()
      this.connected = true
    } catch (err) {
      this.connected = false
      throw err
    }
  }

  async disconnect(): Promise<void> {
    if (this.connection) {
      await this.connection.stop()
      this.connected = false
      this.connection = null
    }
  }

  /** All invokes are silent no-ops while disconnected (mobile guards the same way). */
  async joinConversation(conversationId: string): Promise<void> {
    if (!this.connected || !this.connection) return
    await this.connection.invoke('JoinConversation', conversationId)
  }

  async leaveConversation(conversationId: string): Promise<void> {
    if (!this.connected || !this.connection) return
    await this.connection.invoke('LeaveConversation', conversationId)
  }

  async sendTyping(conversationId: string): Promise<void> {
    if (!this.connected || !this.connection) return
    await this.connection.invoke('TypingAsync', conversationId)
  }

  async markAsRead(conversationId: string): Promise<void> {
    if (!this.connected || !this.connection) return
    // The hub method keeps its Async suffix (like TypingAsync/HeartbeatAsync):
    // invoking 'MarkAsRead' throws HubException "Method does not exist".
    await this.connection.invoke('MarkAsReadAsync', conversationId)
  }

  async heartbeat(): Promise<void> {
    if (!this.connected || !this.connection) return
    await this.connection.invoke('HeartbeatAsync')
  }

  private wire(connection: ReturnType<HubConnectionFactory>): void {
    // Callbacks resolve through `this.events` on every invocation so a later
    // `setEvents` (session wiring) takes effect on live connections.
    connection.on('ReceiveMessage', (...args: unknown[]) => {
      const message = asRecord(args[0])
      if (message) this.events.onMessage?.(message)
    })
    connection.on('ChatEvent', (...args: unknown[]) => {
      const event = asRecord(args[0])
      if (!event) {
        this.events.onError?.(new Error('Malformed ChatEvent: expected a map payload'))
        return
      }
      this.events.onChatEvent?.(event)
    })
    connection.on('UserTyping', (...args: unknown[]) => {
      const pair = hubEventPair(args, 'conversationId', 'userId')
      if (pair) this.events.onTyping?.(pair[0], pair[1])
    })
    connection.on('MessagesRead', (...args: unknown[]) => {
      const pair = hubEventPair(args, 'conversationId', 'userId')
      if (pair) this.events.onMessagesRead?.(pair[0], pair[1])
    })
    connection.on('UserOnline', (...args: unknown[]) => {
      const userId = hubEventString(args, 'userId', 0)
      if (userId) this.events.onUserOnline?.(userId)
    })
    connection.on('UserOffline', (...args: unknown[]) => {
      const userId = hubEventString(args, 'userId', 0)
      if (userId) this.events.onUserOffline?.(userId)
    })
    connection.onclose(() => {
      this.connected = false
    })
    connection.onreconnecting(() => {
      this.connected = false
    })
    connection.onreconnected(() => {
      this.connected = true
      this.events.onReconnected?.()
    })
  }
}
